/*
================================================================================
BRITTEN CUSTOM: Product options engine
================================================================================

Added:       October 2026
Author:      Claude / Matthew
Location:    assets/britten-options.js
Loaded by:   snippets/britten-options.liquid (only on a product page whose
             product has a switched-on option set; defer)

PURPOSE:
Makes the fields snippets/britten-options*.liquid draw work: show-when rules,
required and length checks, the "Optional extras" fold, paid extras, prefill
from a link. Paid extras go into the bag in ONE request with the product, so
they all land or none do (adding them one by one once lost 5 of 62 extras
between Aug and Oct 2026: one add failed and the product went in without it).

HOW IT WORKS:
1. Every [data-britten-options] block inside a product form is one set; every
   [data-britten-opt] inside it is a field: text, select, choice, extra.
   Other types can plug in with BrittenOptions.registerType(name, factory).
2. Show-when: a field with data-show-field is shown only while that field's
   stored value is / contains data-show-value (case does not matter). Hidden
   fields are disabled: nothing is sent and nothing is checked.
3. On the Add to bag click and on submit (capture phase on the form) every
   shown field is checked. A problem stops the add, opens the fold if it is
   shut, shows ONE calm message and moves to the field.
4. No paid extra chosen: nothing else happens; the add goes on exactly as
   before (the bag drawer's add, or the native post).
5. Paid extras chosen:
   - with the bag drawer's add on (window.BrittenBagDrawer.handlesAdd), the
     drawer asks BrittenOptions.itemsFor(form, formData) and posts those
     items as its ONE add, then opens on "Added to your bag";
   - otherwise this script posts the same items itself and opens the bag page.
   items = the product (every property the form holds, plus _io_order_group)
   and each extra (_io_parent_order_group = the same id, _io_field_name = the
   field's property name, quantity = the product's). Those are the keys the
   bag (Cart 4a) and Maker OS read, and older bags carry the same keys.
6. If an add with extras is refused or fails, nothing is posted again (a
   plain re-post would add the product without its extras): the drawer calls
   BrittenOptions.addFailed(form) and the bride sees a calm message.
7. ?bo_<field id>=value on the page link fills a field in.

ORDER DATA STAYS ENGLISH: property names and stored values never change with
the language. Paid fields store their value in a hidden input that only this
script changes, so without it the order says "No thank you".

DEBUG: add ?bo_debug=1 to the page link.

CHANGELOG:
- 15 Oct 2026: created (bracelet branch).
- 31 Oct 2026: select, priced choice, extra tick cards, show-when, fold,
  character count, the drawer hand-over (itemsFor / addFailed).

================================================================================
*/
(function (root) {
  'use strict';

  // === CONFIGURATION ===
  var DEBUG = /[?&]bo_debug=1/.test(root.location ? root.location.search : '');
  var P_GROUP = '_io_order_group';
  var P_PARENT = '_io_parent_order_group';
  var P_FIELD = '_io_field_name';
  var ADD_BUTTON = '[data-add-to-cart-trigger], .button--add-to-cart, button[name="add"]';

  // === HELPERS ===
  function log() {
    if (!DEBUG || !root.console) { return; }
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Britten Options]');
    root.console.log.apply(root.console, args);
  }

  function interpolate(str, vars) {
    return String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (all, name) {
      return (vars && vars[name] != null) ? String(vars[name]) : all;
    });
  }

  function charCount(s) {
    return Array.from ? Array.from(String(s || '')).length : String(s || '').length;
  }

  function query() {
    try { return new URLSearchParams(root.location.search); } catch (err) { return null; }
  }

  function withSlash(url) {
    url = String(url || '/');
    return url.charAt(url.length - 1) === '/' ? url : url + '/';
  }

  function newGroupId() {
    // A millisecond time, plus a little randomness so two adds in one
    // millisecond can never share a group.
    return String(Date.now()) + String(Math.floor(Math.random() * 900) + 100);
  }

  function matches(value, op, want) {
    var v = String(value == null ? '' : value).toLowerCase();
    var w = String(want == null ? '' : want).toLowerCase();
    return op === 'contains' ? v.indexOf(w) !== -1 : v === w;
  }

  function setDisabled(el, on) {
    var inputs = el.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      // A paid choice's radios never post (their hidden value input does).
      inputs[i].disabled = on;
    }
  }

  // === FIELD TYPES ===
  // A factory returns { value(), validate(), focus(), prefill(v), extra() }.
  //   value()  -> the stored value now (for show-when rules)
  //   extra()  -> null or { id: <variant id>, key: <property name> }
  var registry = {};

  function maxOf(el) { return parseInt(el.getAttribute('data-max'), 10) || 0; }

  function checkText(api, el, value, required) {
    var v = String(value || '').trim();
    var max = maxOf(el);
    if (required && !v) { return api.t('required', { field: api.label(el) }); }
    if (max && charCount(v) > max) { return api.t('tooLong', { field: api.label(el), max: max }); }
    return '';
  }

  registry.text = function (el, api) {
    var input = el.querySelector('[data-britten-opt-text]');
    if (!input) { return null; }
    var required = el.getAttribute('data-required') === 'true';
    var count = el.querySelector('[data-britten-opt-count]');
    var max = maxOf(el);
    function paintCount() {
      if (!count || !max) { return; }
      var n = charCount(input.value);
      count.hidden = n === 0;
      count.textContent = api.t('count', { n: n, max: max });
    }
    input.addEventListener('input', function () { api.clearError(); paintCount(); api.changed(); });
    paintCount();
    return {
      value: function () { return input.value; },
      validate: function () { return checkText(api, el, input.value, required); },
      focus: function () { input.focus(); },
      prefill: function (v) { input.value = v; paintCount(); },
      extra: function () { return null; }
    };
  };

  registry.select = function (el, api) {
    var sel = el.querySelector('[data-britten-opt-select]');
    if (!sel) { return null; }
    sel.addEventListener('change', function () { api.clearError(); api.changed(); });
    return {
      value: function () { return sel.value; },
      validate: function () { return ''; },
      focus: function () { sel.focus(); },
      prefill: function (v) {
        for (var i = 0; i < sel.options.length; i++) {
          if (sel.options[i].value.toLowerCase() === String(v).toLowerCase()) { sel.selectedIndex = i; }
        }
      },
      extra: function () { return null; }
    };
  };

  registry.choice = function (el, api) {
    var radios = el.querySelectorAll('input[type="radio"]');
    if (!radios.length) { return null; }
    var hidden = el.querySelector('[data-britten-opt-value]');   /* paid choices only */
    function checked() {
      for (var i = 0; i < radios.length; i++) { if (radios[i].checked) { return radios[i]; } }
      return null;
    }
    function sync() {
      var r = checked();
      if (hidden) { hidden.value = r ? r.value : (radios[0] ? radios[0].value : ''); }
    }
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () { sync(); api.clearError(); api.changed(); });
    }
    sync();
    return {
      value: function () { var r = checked(); return r ? r.value : ''; },
      validate: function () { return ''; },
      focus: function () { (checked() || radios[0]).focus(); },
      prefill: function (v) {
        for (var i = 0; i < radios.length; i++) {
          if (radios[i].value.toLowerCase() === String(v).toLowerCase()) { radios[i].checked = true; }
        }
        sync();
      },
      extra: function () {
        if (!hidden) { return null; }
        var r = checked();
        var id = r ? parseInt(r.getAttribute('data-addon-variant'), 10) : 0;
        return id ? { id: id, key: el.getAttribute('data-key') || '' } : null;
      }
    };
  };

  registry.extra = function (el, api) {
    var toggle = el.querySelector('[data-britten-opt-toggle]');
    var hidden = el.querySelector('[data-britten-opt-value]');
    if (!toggle || !hidden) { return null; }
    var yes = el.getAttribute('data-yes') || 'Yes please';
    var no = el.getAttribute('data-no') || 'No thank you';
    function sync() {
      hidden.value = toggle.checked ? yes : no;
      el.classList.toggle('britten-opt--on', toggle.checked);
    }
    toggle.addEventListener('change', function () { sync(); api.clearError(); api.changed(); });
    sync();
    return {
      value: function () { return hidden.value; },
      validate: function () { return ''; },
      focus: function () { toggle.focus(); },
      prefill: function (v) { toggle.checked = /^(1|yes|true|on)/i.test(String(v)); sync(); },
      extra: function () {
        if (!toggle.checked) { return null; }
        var id = parseInt(el.getAttribute('data-addon-variant'), 10);
        return id ? { id: id, key: el.getAttribute('data-key') || '' } : null;
      }
    };
  };

  // === ONE SET ===
  var sets = [];

  function setUp(block) {
    if (block.__brittenOptions) { return; }
    var form = block.closest('form');
    if (!form) { log('no form around the set'); return; }

    var stringsEl = block.querySelector('[data-britten-options-strings]');
    var strings = stringsEl ? stringsEl.dataset : {};
    var errorEl = block.querySelector('[data-britten-opts-error]');
    var foldBtn = block.querySelector('[data-britten-opts-fold]');
    var panel = block.querySelector('[data-britten-opts-panel]');
    var fields = [];
    var byId = {};
    var busy = false;

    function showError(message, field) {
      if (errorEl) {
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
      }
      for (var i = 0; i < fields.length; i++) {
        fields[i].el.classList.toggle('britten-opt--invalid', !!(field && fields[i] === field));
      }
    }

    function setFold(open) {
      if (!foldBtn || !panel) { return; }
      panel.hidden = !open;
      foldBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      foldBtn.classList.toggle('britten-addons-toggle--open', open);
    }

    function isShown(f) { return !f.el.hidden; }

    // Show-when: run until nothing changes (a shown field can control another).
    function applyRules() {
      for (var pass = 0; pass < 4; pass++) {
        var changed = false;
        for (var i = 0; i < fields.length; i++) {
          var f = fields[i];
          var ctl = f.el.getAttribute('data-show-field');
          if (!ctl) { continue; }
          var src = byId[ctl];
          var on = !!src && isShown(src) &&
            matches(src.value(), f.el.getAttribute('data-show-op'), f.el.getAttribute('data-show-value'));
          if (on === isShown(f)) { continue; }
          f.el.hidden = !on;
          setDisabled(f.el, !on);
          changed = true;
        }
        if (!changed) { return; }
      }
    }

    var api = {
      form: form,
      block: block,
      t: function (name, vars) { return interpolate(strings[name] || '', vars); },
      label: function (el) { return el.getAttribute('data-label') || ''; },
      clearError: function () { if (errorEl && !errorEl.hidden) { showError(''); } },
      changed: function () { applyRules(); },
      log: log
    };

    var nodes = block.querySelectorAll('[data-britten-opt]');
    for (var n = 0; n < nodes.length; n++) {
      var el = nodes[n];
      var type = el.getAttribute('data-britten-opt');
      var factory = registry[type];
      if (!factory) { log('no handler for type', type); continue; }
      try {
        var handler = factory(el, api);
        if (handler) {
          handler.el = el;
          fields.push(handler);
          var fid = el.getAttribute('data-id');
          if (fid) { byId[fid] = handler; }
        }
      } catch (err) {
        log('field failed to start', type, err);
      }
    }

    if (foldBtn) {
      foldBtn.addEventListener('click', function () {
        var opening = foldBtn.getAttribute('aria-expanded') !== 'true';
        setFold(opening);
        if (opening) {
          try { document.dispatchEvent(new CustomEvent('britten:metric', { detail: { name: 'pdp_addons_open' } })); } catch (err) { /* old browser */ }
        }
      });
    }

    // Enter in one of our boxes must never add to bag.
    block.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') { return; }
      var tag = e.target && e.target.tagName;
      if (tag === 'INPUT' && e.target.type !== 'checkbox' && e.target.type !== 'radio') { e.preventDefault(); }
    });

    // Prefill from the link: ?bo_<field id>=value. A prefilled field in a
    // shut fold opens it, so she sees what the link chose.
    var q = query();
    if (q) {
      fields.forEach(function (f) {
        var id = f.el.getAttribute('data-id');
        var value = id ? q.get('bo_' + id) : null;
        if (value && f.prefill) {
          try { f.prefill(value, q); setFold(true); } catch (err) { log('prefill failed', id, err); }
        }
      });
    }
    applyRules();

    function firstProblem() {
      for (var i = 0; i < fields.length; i++) {
        if (!isShown(fields[i])) { continue; }
        var message = '';
        try { message = fields[i].validate ? fields[i].validate() : ''; } catch (err) { log(err); }
        if (message) { return { field: fields[i], message: message }; }
      }
      return null;
    }

    function extras() {
      var out = [];
      for (var i = 0; i < fields.length; i++) {
        if (!isShown(fields[i])) { continue; }
        var a = fields[i].extra ? fields[i].extra() : null;
        if (a) { out.push(a); }
      }
      return out;
    }

    function stop(e) {
      e.preventDefault();
      if (e.stopImmediatePropagation) { e.stopImmediatePropagation(); }
    }

    function drawerTakesIt() {
      return !!(root.BrittenBagDrawer && root.BrittenBagDrawer.handlesAdd === true);
    }

    function guard(e) {
      if (busy) { stop(e); return; }
      var problem = firstProblem();
      if (problem) {
        stop(e);
        setFold(true);
        showError(problem.message, problem.field);
        try { problem.field.el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* old browser */ }
        if (problem.field.focus) { problem.field.focus(); }
        log('add stopped:', problem.message);
        return;
      }
      showError('');
      if (!extras().length) { return; }   /* plain add: nothing to do */
      if (drawerTakesIt()) { return; }      /* the drawer posts itemsFor() as its one add */
      if (e.type === 'click') { return; }   /* wait for the submit it causes */
      stop(e);
      addOurselves();
    }

    form.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest(ADD_BUTTON) : null;
      if (!btn || !form.contains(btn)) { return; }
      var problem = firstProblem();
      if (problem || busy) { guard(e); }
    }, true);
    form.addEventListener('submit', guard, true);

    // The add, as items: the product, then its extras. formData is the form's
    // own FormData (the drawer passes the one it built with the submitter).
    function items(formData) {
      var list = extras();
      if (!list.length) { return null; }
      var data = formData || new FormData(form);
      var props = {};
      var variantId = null;
      var quantity = 1;
      data.forEach(function (value, name) {
        if (typeof value !== 'string') { return; }
        if (name === 'id') { variantId = parseInt(value, 10); return; }
        if (name === 'quantity') { quantity = Math.max(1, parseInt(value, 10) || 1); return; }
        var m = /^properties\[(.+)\]$/.exec(name);
        if (m && value !== '') { props[m[1]] = value; }
      });
      if (!variantId) { return null; }
      var group = newGroupId();
      props[P_GROUP] = group;
      var out = [{ id: variantId, quantity: quantity, properties: props }];
      list.forEach(function (a) {
        var p = {};
        p[P_PARENT] = group;
        p[P_FIELD] = a.key;
        out.push({ id: a.id, quantity: quantity, properties: p });
      });
      log('items', out);
      return out;
    }

    function failed() {
      busy = false;
      showError(strings.addError || '', null);
      // She may have tapped the mobile sticky bar, far below the fields.
      try { if (errorEl) { errorEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } } catch (err) { /* old browser */ }
    }

    // No drawer: post the items ourselves, then open the bag page.
    function addOurselves() {
      var list = items(null);
      if (!list) { return; }
      busy = true;
      var btn = form.querySelector(ADD_BUTTON);
      var label = btn ? btn.querySelector('.text') : null;
      var before = label ? label.textContent : '';
      if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
      if (label && strings.adding) { label.textContent = strings.adding; }
      var base = withSlash(block.getAttribute('data-root-url') || (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/');
      var cartUrl = block.getAttribute('data-cart-url') || (base + 'cart');
      function restore() {
        if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
        if (label) { label.textContent = before; }
      }
      root.fetch(base + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: list })
      }).then(function (res) {
        if (!res.ok) { throw new Error('cart/add ' + res.status); }
        return res.json();
      }).then(function () {
        restore();
        busy = false;
        root.location.href = cartUrl;
      }).catch(function (err) {
        log('add failed', err);
        restore();
        failed();
      });
    }

    var set = { form: form, block: block, items: items, failed: failed };
    block.__brittenOptions = set;
    sets.push(set);
    log('set ready:', block.getAttribute('data-britten-options'), fields.length + ' fields');
  }

  function setFor(form) {
    for (var i = 0; i < sets.length; i++) { if (sets[i].form === form) { return sets[i]; } }
    return null;
  }

  // === BOOT ===
  function boot() {
    var blocks = document.querySelectorAll('[data-britten-options]');
    for (var i = 0; i < blocks.length; i++) { setUp(blocks[i]); }
  }

  root.BrittenOptions = {
    version: 2,
    registerType: function (name, factory) { registry[name] = factory; },
    boot: boot,
    /* For the bag drawer: the items for ONE add of this form (product first),
       or null when no paid extra is chosen (then the plain add is right). */
    itemsFor: function (form, formData) {
      var s = setFor(form);
      return s ? s.items(formData) : null;
    },
    /* For the bag drawer: an add with extras was refused or did not land.
       Shows the calm message; nothing is re-posted. */
    addFailed: function (form) {
      var s = setFor(form);
      if (s) { s.failed(); }
    },
    _matches: matches
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
